<template>
  <div class="dashboard-container">
    
    <!-- Desktop Header -->
    <header class="slim-header blue-grey darken-3 z-depth-4">
      <div class="container slim-header-wrapper">
        <div class="slim-profile-section">
          <div class="slim-avatar-wrapper">
            <img 
              :src="user?.profile_img || `https://ui-avatars.com/api/?name=${user?.name}&background=0f172a&color=fff&bold=true`"
              class="circle slim-avatar"
              :alt="user?.name"
            />
            <span class="status-dot"></span>
          </div>
          <div class="slim-text-group">
            <h6 class="slim-welcome">
              Hello, <b>{{ getProfileDisplay(user?.name) }}</b>
            </h6>
            <span class="slim-designation">
              {{ user?.designation || 'Administrator' }}
            </span>
          </div>
        </div>

        <div class="slim-actions">
          <div class="slim-action-buttons">
            <button class="slim-btn" @click="handleRefresh">
              <i class="material-icons">refresh</i>
            </button>

            <div class="action-divider"></div>

            <button class="slim-btn logout-btn" @click="handleSignOut">
              <i class="material-icons">power_settings_new</i>
              <span>Logout</span>
            </button>
          </div>

          <button
            class="slim-btn notification-enable-btn"
            v-if="notificationPromptVisible"
            @click="handleEnableNotifications"
          >
            <i class="material-icons">notifications_active</i>
            <span>{{ notificationLoading ? "Enabling..." : "Enable Alerts" }}</span>
          </button>
        </div>
      </div>
    </header>

    <!-- Main -->
    <main class="main-content">
      <div class="container">
        <!-- Loading -->
        <div v-if="isLoading" class="center-align loading-state">
          <div class="preloader-wrapper active">
            <div class="spinner-layer spinner-teal">
              <div class="circle-clipper left"><div class="circle"></div></div>
              <div class="gap-patch"><div class="circle"></div></div>
              <div class="circle-clipper right"><div class="circle"></div></div>
            </div>
          </div>
          <p class="grey-text">Loading modules...</p>
        </div>

        <!-- Error -->
        <div v-else-if="error" class="center-align error-state">
          <i class="large material-icons red-text">error_outline</i>
          <h5>Something went wrong</h5>
          <p>{{ error }}</p>
          <button class="btn" @click="loadModules">Retry</button>
        </div>

        <div v-if="!isLoading && !error" class="module-list-header">
          <span class="module-list-kicker">Workspace</span>
        </div>

        <!-- Modules -->
        <section v-if="!isLoading && !error && tabs.length" class="module-list-shell">
          <div class="module-list">
            <button
              v-for="(tab, index) in tabs"
              :key="tab.id"
              class="module-row-card"
              :style="{ animationDelay: `${index * 0.035}s` }"
              type="button"
              @click="navigateToModule(tab.to)"
            >
              <span class="module-row-icon">
                <i class="material-icons">{{ tab.icon }}</i>
              </span>

              <span class="module-row-copy">
                <span class="module-row-title">{{ tab.title }}</span>
                <span v-if="tab.description" class="module-row-desc">
                  {{ tab.description }}
                </span>
              </span>

              <span v-if="tab.notification" class="module-row-alert">
                {{ tab.notification }}
              </span>

              <span class="module-row-action">
                <span>Open Module</span>
                <i class="material-icons">arrow_forward</i>
              </span>
            </button>
          </div>
        </section>

        <section v-else-if="!isLoading && !error" class="module-list-shell">
          <div class="module-empty-state">
            <div class="module-empty-icon">
              <i class="material-icons">apps</i>
            </div>
            <div>
              <h6>No modules available</h6>
              <p>Your profile does not currently have active workspace modules.</p>
            </div>
          </div>
        </section>

        <footer v-if="!isLoading && !error" class="dashboard-footer">
          <span>easyHMO.com.ng</span>
          <span class="dashboard-footer-divider"></span>
          <span>Workspace</span>
        </footer>

      </div>
    </main>
  </div>
</template>

<script>
import { onMounted, onUnmounted, ref, computed } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import getItems from "@/composables/getItems";
import postItem from "@/composables/postItem";
import { siloMode } from "@/laravel/config";
import { addModules } from "@/composables/cache/modules";
import { clearCacheOnly } from "@/composables/cache/logout";
import {
  clearCachedPayload,
  getCachedPayload,
  setCachedPayload,
} from "@/composables/requestOptimization";
import {
  isMessagingReady,
  listenForForegroundMessages,
  registerNotificationToken,
  syncNotificationToken,
} from "@/composables/useFirebaseMessaging";
import {
  reconcileAuthorizationHomeCount,
  registerAuthorizationRealtimeTarget,
} from "@/composables/authorizationRealtimeCoordinator";

const COLOR_PALETTE = ["teal", "blue", "indigo", "purple"];
const MODULE_COLORS = COLOR_PALETTE.map(color => `${color}-accent`);
const HOME_REFRESH_THROTTLE = 15 * 60 * 1000;
const STARTER_CACHE_TTL = HOME_REFRESH_THROTTLE;

export default {
  name: "HomeView",
  setup() {
    const router = useRouter();
    const store = useStore();

    const tabs = ref([]);
    const isLoading = ref(true);
    const error = ref(null);
    const notificationReady = ref(false);
    const notificationPermission = ref("default");
    const notificationLoading = ref(false);

    const user = computed(() => store.state.user || null);
    const userModules = computed(() =>
      Array.isArray(user.value?.modules) ? user.value.modules : []
    );

    const notificationPromptVisible = computed(() =>
      notificationReady.value &&
      notificationPermission.value === "default" &&
      userModules.value.includes("auth")
    );
    let starterRequestInFlight = false;

    onMounted(() => {
      document.title = "Admin Dashboard";
      loadModules();
      prepareNotifications();
    });

    const getProfileDisplay = (fullName) => {
      if (!fullName) return "";
      const name = fullName.trim().split(/[.\s]+/)[0];
      return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
    };

    const loadModules = async () => {
      try {
        isLoading.value = true;
        error.value = null;
        const sourceTabs = addModules();
        const isFreshSignin = store.state.dashboardFreshSignin;

        if (user.value) {
          tabs.value = sourceTabs
            .filter(tab =>
              (tab.to === "help" || userModules.value.includes(tab.to)) &&
              tab.type === user.value.type
            )
            .map((tab, index) => ({
              ...tab,
              bgColor: MODULE_COLORS[index % MODULE_COLORS.length],
              notification: null
            }));

          const lastRefresh = Number(window.localStorage.getItem(homeRefreshKey()) || 0);
          const shouldRefresh = isFreshSignin || !lastRefresh || Date.now() - lastRefresh >= HOME_REFRESH_THROTTLE;
          const refreshed = await loadNotifications({ force: shouldRefresh });

          if (shouldRefresh && refreshed) {
            window.localStorage.setItem(homeRefreshKey(), String(Date.now()));
          }
        }
      } catch (err) {
        error.value = "Failed to load modules";
      } finally {
        store.commit("loadDashboardFreshSignin", false);
        isLoading.value = false;
      }
    };

    const applyNotifications = (items) => {
      const map = new Map(
        items.map(item => [item.name, item.message])
      );
      tabs.value = tabs.value.map(tab => ({
        ...tab,
        notification: map.get(tab.to) || null
      }));
    };

    const incrementAuthorizationCount = () => {
      let updated = false;
      let nextMessage = null;

      tabs.value = tabs.value.map(tab => {
        if (tab.to !== "auth") return tab;

        const currentCount = Number.parseInt(tab.notification, 10) || 0;
        nextMessage = `${currentCount + 1} Pre-auth request(s) pending`;
        updated = true;
        return {
          ...tab,
          notification: nextMessage,
        };
      });

      const cacheKey = starterCacheKey();
      const cachedPayload = cacheKey ? getCachedPayload(cacheKey) : null;

      if (updated && Array.isArray(cachedPayload)) {
        const hasAuth = cachedPayload.some(item => item.name === "auth");
        const nextPayload = cachedPayload.map(item => item.name === "auth"
          ? { ...item, message: nextMessage }
          : item
        );

        if (!hasAuth) {
          nextPayload.push({
            id: nextPayload.length + 1,
            name: "auth",
            message: nextMessage,
          });
        }

        setCachedPayload(cacheKey, nextPayload, STARTER_CACHE_TTL);
      }

      return updated;
    };

    const applyAuthNotification = (items) => {
      const auth = items.find(item => item.name === "auth");
      if (!auth) return;

      tabs.value = tabs.value.map(tab => tab.to === "auth"
        ? { ...tab, notification: auth.message || null }
        : tab
      );
    };

    const starterCacheKey = () => {
      if (!user.value?.id) {
        return null;
      }

      const authSource = siloMode ? "easyhmo" : "oneprovider";
      return `operator-starter:${user.value.id}:${authSource}`;
    };

    const homeRefreshKey = () => `${starterCacheKey()}:last-home-refresh`;

    const loadNotifications = async ({ force = false, authOnly = false } = {}) => {
      try {
        const cacheKey = starterCacheKey();

        if (!cacheKey || starterRequestInFlight) return false;

        const cachedPayload = !force ? getCachedPayload(cacheKey) : null;
        if (cachedPayload) {
          authOnly ? applyAuthNotification(cachedPayload) : applyNotifications(cachedPayload);
          return true;
        }

        starterRequestInFlight = true;
        const authSource = siloMode ? "easyhmo" : "oneprovider";
        const { res } = await getItems(`operator/starter?u=${user.value.id}&auth_source=${authSource}`);

        if (res.value?.data) {
          authOnly ? applyAuthNotification(res.value.data) : applyNotifications(res.value.data);
          reconcileAuthorizationHomeCount();
          setCachedPayload(cacheKey, res.value.data, STARTER_CACHE_TTL);
          return true;
        }
      } catch (err) {
        // Notification counts should not block dashboard access.
      } finally {
        starterRequestInFlight = false;
      }

      return false;
    };

    const unregisterRealtimeTarget = registerAuthorizationRealtimeTarget("home", {
      incrementAuthorizationCount,
    });
    onUnmounted(unregisterRealtimeTarget);

    const handleRefresh = async () => {
      if (!siloMode) {
        await postItem("integration/oneprovider/claims/sync", { force: true });
      }

      clearCachedPayload(starterCacheKey());
      clearCacheOnly();
      router.push({ name: "loading" });
    };

    const handleSignOut = () => {
      router.push({ name: "logout" });
    };

    const navigateToModule = (route) => router.push({ name: route });

    const prepareNotifications = async () => {
      notificationReady.value = await isMessagingReady();
      notificationPermission.value = "Notification" in window ? Notification.permission : "denied";

      if (!notificationReady.value) {
        return;
      }

      if (notificationPermission.value === "granted") {
        await syncNotificationToken();
      }

      listenForForegroundMessages((payload) => {
        const title = payload?.notification?.title || "EasyHMO";
        const body = payload?.notification?.body || "New notification received.";

        if ("Notification" in window && Notification.permission === "granted") {
          new Notification(title, {
            body,
            icon: "/img/icons/android-chrome-192x192.png",
          });
        }
      });
    };

    const handleEnableNotifications = async () => {
      notificationLoading.value = true;
      const result = await registerNotificationToken();
      notificationPermission.value = "Notification" in window ? Notification.permission : result.status;
      notificationLoading.value = false;
    };

    return {
      tabs,
      isLoading,
      error,
      user,
      notificationLoading,
      notificationPromptVisible,
      getProfileDisplay,
      handleEnableNotifications,
      handleRefresh,
      handleSignOut,
      navigateToModule,
      loadModules
    };
  }
};
</script>

<style scoped>
.dashboard-container {
  min-height: 100vh;
  background:
    radial-gradient(circle at 12% 10%, rgba(0, 150, 136, 0.045) 0, rgba(0, 150, 136, 0) 28%),
    radial-gradient(circle at 88% 18%, rgba(55, 71, 79, 0.032) 0, rgba(55, 71, 79, 0) 24%),
    linear-gradient(135deg, rgba(196, 211, 217, 0.11) 0 1px, transparent 1px 36px),
    linear-gradient(45deg, rgba(196, 211, 217, 0.075) 0 1px, transparent 1px 44px),
    #fafafa;
  background-size: auto, auto, 36px 36px, 44px 44px, auto;
  overflow-x: hidden;
}

/* ===== HEADER ===== */
.slim-header {
  background: #37474f;
  padding: 14px 0;
  color: white;
}

.slim-header-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.slim-profile-section {
  display: flex;
  align-items: center;
  gap: 14px;
}

.slim-avatar-wrapper {
  flex: 0 0 auto;
  position: relative;
}

.slim-avatar {
  width: 42px;
  height: 42px;
  border: 2px solid white;
}

.status-dot {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 10px;
  height: 10px;
  background: #22c55e;
  border-radius: 50%;
  border: 2px solid #37474f;
}

.slim-welcome {
  margin: 0;
  font-size: 1rem;
}

.slim-designation {
  font-size: 0.75rem;
  opacity: 0.7;
  text-transform: uppercase;
}

.slim-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.slim-action-buttons {
  align-items: center;
  display: flex;
  gap: 12px;
}

.slim-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 6px;
  transition: 0.2s ease;
}

.slim-btn:hover {
  background: rgba(255,255,255,0.1);
}

.action-divider {
  width: 1px;
  height: 18px;
  background: rgba(255,255,255,0.2);
}

.main-content {
  padding-bottom: 8px;
}

/* ===== STATES ===== */
.loading-state {
  margin-top: 46px;
}

.loading-state p {
  color: #607d8b !important;
}

.error-state {
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(255, 255, 255, 0.72);
  border-radius: 12px;
  box-shadow: 0 18px 38px rgba(15, 23, 42, 0.2);
  margin: 36px auto 0;
  max-width: 720px;
  padding: 34px 26px;
}

/* ===== MODULE ROWS ===== */
.module-list-shell {
  background: transparent;
  border-radius: 0;
  margin: 34px auto 0;
  max-width: 980px;
  padding: 0;
  position: relative;
}

.module-list-shell::before {
  content: none;
}

.module-list-shell > * {
  position: relative;
  z-index: 1;
}

.module-list {
  display: grid;
  gap: 14px;
  padding: 0 0 30px;
  position: relative;
}

.module-list::before {
  border: 5px solid #fafafa;
  border-radius: 24px;
  bottom: 8px;
  box-shadow: none;
  content: "";
  left: -28px;
  pointer-events: none;
  position: absolute;
  right: -28px;
  top: -22px;
}

.module-list > * {
  position: relative;
  z-index: 1;
}

.module-list-header {
  align-items: center;
  background: #ffffff;
  border: 1px solid #e1e8eb;
  border-bottom: 1px solid #c4d3d9;
  border-radius: 0 0 10px 10px;
  border-top: 0;
  box-shadow: 0 14px 24px -24px rgba(38, 50, 56, 0.5);
  box-sizing: border-box;
  display: inline-flex;
  margin: 0;
  min-height: 34px;
  padding: 9px 18px 8px;
}

.module-list-kicker {
  color: #00897b;
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1;
  margin: 0;
  text-transform: uppercase;
}

.module-row-card {
  align-items: center;
  animation: fadeUp 0.35s ease forwards;
  background: #ffffff;
  border: 1px solid rgba(222, 234, 238, 0.96);
  border-radius: 12px;
  box-shadow: none;
  color: inherit;
  cursor: pointer;
  display: grid;
  gap: 18px;
  grid-template-columns: 58px minmax(0, 1fr) auto auto;
  min-height: 92px;
  opacity: 0;
  padding: 18px 20px;
  text-align: left;
  transform: translateY(8px);
  transition: border-color 0.22s ease, box-shadow 0.22s ease, transform 0.22s ease, background 0.22s ease;
  width: 100%;
}

.module-row-card:hover {
  background: #fbfcfc;
  border-color: #c4d3d9;
  box-shadow: none;
  transform: none;
}

@keyframes fadeUp {
  to { opacity: 1; transform: translateY(0); }
}

.module-row-icon {
  align-items: center;
  background: #eef7f6;
  border: 1px solid #d5e9e7;
  border-radius: 12px;
  color: #334155;
  display: inline-flex;
  height: 52px;
  justify-content: center;
  transition: background 0.2s ease, color 0.2s ease;
  width: 52px;
}

.module-row-icon i {
  font-size: 1.55rem;
}

.module-row-card:hover .module-row-icon {
  background: #37474f;
  color: #ffffff;
}

.module-row-copy {
  display: flex;
  flex-direction: column;
  gap: 7px;
  min-width: 0;
}

.module-row-title {
  color: #111827;
  font-size: 1.12rem;
  font-weight: 800;
  line-height: 1.2;
}

.module-row-desc {
  color: #64748b;
  font-size: 0.92rem;
  line-height: 1.45;
}

.module-row-alert {
  background: #fef3c7;
  border-radius: 999px;
  color: #92400e;
  font-size: 0.78rem;
  font-weight: 700;
  max-width: 260px;
  padding: 9px 16px;
  text-align: center;
}

.module-row-action {
  align-items: center;
  color: #1f2937;
  display: inline-flex;
  gap: 10px;
  font-size: 0.78rem;
  font-weight: 800;
  justify-content: flex-end;
  white-space: nowrap;
}

.module-row-action i {
  align-items: center;
  background: #eef3f5;
  border-radius: 9px;
  color: #0f172a;
  display: inline-flex;
  height: 34px;
  justify-content: center;
  transition: background 0.2s ease, color 0.2s ease;
  width: 34px;
}

.module-row-card:hover .module-row-action i {
  background: #37474f;
  color: #ffffff;
}

.module-empty-state {
  align-items: center;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.055);
  display: flex;
  gap: 16px;
  padding: 24px;
}

.module-empty-icon {
  align-items: center;
  background: #edf3f8;
  border-radius: 12px;
  color: #334155;
  display: flex;
  flex: 0 0 auto;
  height: 52px;
  justify-content: center;
  width: 52px;
}

.module-empty-state h6 {
  color: #111827;
  font-weight: 800;
  margin: 0 0 6px;
}

.module-empty-state p {
  color: #64748b;
  margin: 0;
}

/* ===== FOOTER ===== */
.dashboard-footer {
  align-items: center;
  color: #78909c;
  display: flex;
  font-size: 0.78rem;
  font-weight: 700;
  gap: 10px;
  justify-content: center;
  letter-spacing: 0;
  margin: 18px auto 0;
  max-width: 980px;
  padding: 22px 0 34px;
}

.dashboard-footer-divider {
  background: #c4d3d9;
  display: inline-block;
  height: 14px;
  width: 1px;
}

@media (min-width: 801px) and (max-width: 1100px) {
  .slim-header-wrapper,
  .main-content .container,
  .dashboard-footer {
    max-width: 860px;
  }

  .module-list-shell {
    margin-top: 30px;
  }

  .module-list {
    gap: 13px;
    padding-bottom: 28px;
  }

  .module-list::before {
    border-radius: 20px;
    left: -13px;
    right: -13px;
  }

  .module-row-card {
    gap: 18px;
    grid-template-columns: 56px minmax(0, 1fr) minmax(180px, auto);
    min-height: 104px;
    padding: 18px 20px;
  }

  .module-row-alert {
    justify-self: end;
    max-width: 260px;
  }

  .module-row-action {
    grid-column: 3;
    justify-self: end;
  }
}

@media (min-width: 601px) and (max-width: 800px) {
  .slim-header-wrapper,
  .main-content .container,
  .dashboard-footer {
    max-width: 680px;
  }

  .slim-header-wrapper {
    flex-wrap: wrap;
  }

  .slim-profile-section {
    flex: 1 1 auto;
    min-width: 320px;
  }

  .notification-enable-btn {
    flex: 0 0 auto;
    margin-top: 0;
    order: 0;
  }

  .module-row-card {
    grid-template-columns: 52px minmax(0, 1fr) minmax(150px, auto);
  }

  .module-row-alert {
    grid-column: 2 / 4;
    justify-self: start;
  }

  .module-row-action {
    grid-column: 3;
  }
}

@media (max-width: 800px) {
  .slim-header {
    padding: 12px 0;
  }

  .slim-header-wrapper {
    gap: 14px;
  }

  .slim-actions {
    gap: 7px;
  }

  .slim-action-buttons {
    gap: 7px;
  }

  .slim-btn {
    min-height: 40px;
    padding: 8px 10px;
  }

  .notification-enable-btn {
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.14);
  }

  .module-list-shell {
    margin-top: 28px;
  }

  .module-list {
    padding-bottom: 26px;
  }

  .module-list::before {
    border-width: 3px;
    border-radius: 18px;
    bottom: 4px;
    left: -10px;
    right: -10px;
    top: -14px;
  }

  .module-list-header {
    padding: 9px 14px 8px;
  }

  .module-row-card {
    border-width: 2px;
    grid-template-columns: 52px minmax(0, 1fr);
  }

  .module-row-card:hover .module-row-icon {
    background: #eef7f6;
    color: #334155;
  }

  .module-row-alert,
  .module-row-action {
    grid-column: 2;
    justify-self: start;
  }

  .dashboard-footer {
    padding-bottom: 26px;
  }
}

@media (min-width: 601px) and (max-width: 800px) {
  .module-row-alert {
    grid-column: 2 / 4;
  }

  .module-row-action {
    grid-column: 3;
    justify-self: end;
  }
}

@media (max-width: 600px) {
  .dashboard-container {
    background-size: auto, auto, 30px 30px, 36px 36px, auto;
  }

  .slim-header {
    padding: 10px 0;
  }

  .slim-header-wrapper {
    align-items: center;
    display: grid;
    gap: 9px 10px;
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .slim-profile-section {
    grid-column: 1;
    grid-row: 1;
    gap: 10px;
    min-width: 0;
  }

  .slim-avatar {
    height: 38px;
    width: 38px;
  }

  .slim-text-group {
    min-width: 0;
  }

  .slim-welcome,
  .slim-designation {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .slim-welcome {
    font-size: 0.92rem;
  }

  .slim-designation {
    font-size: 0.68rem;
    margin-top: 3px;
  }

  .slim-actions {
    display: contents;
  }

  .slim-action-buttons {
    align-items: center;
    display: flex;
    gap: 6px;
    grid-column: 2;
    grid-row: 1;
    justify-self: end;
  }

  .slim-btn {
    border-radius: 8px;
    justify-content: center;
    min-height: 38px;
    padding: 7px 9px;
  }

  .slim-btn i {
    font-size: 21px;
  }

  .logout-btn span {
    display: none;
  }

  .notification-enable-btn {
    grid-column: 1 / -1;
    justify-content: center;
    margin-top: 0;
    min-width: 0;
    width: 100%;
  }

  .notification-enable-btn span {
    display: inline;
    font-size: 0.78rem;
    font-weight: 700;
  }

  .action-divider {
    display: none;
  }

  .main-content {
    padding-bottom: 0;
  }

  .loading-state,
  .error-state {
    margin-top: 34px;
  }

  .error-state {
    border-radius: 10px;
    padding: 26px 18px;
  }

  .module-list-header {
    border-radius: 0 0 9px 9px;
    min-height: 32px;
    padding: 8px 13px 7px;
  }

  .module-list-kicker {
    font-size: 0.68rem;
  }

  .module-list-shell {
    margin-top: 24px;
  }

  .module-list {
    gap: 11px;
    padding-bottom: 18px;
  }

  .module-list::before {
    border-width: 2px;
    border-radius: 16px;
    bottom: -2px;
    left: -7px;
    right: -7px;
    top: -11px;
  }

  .module-row-card {
    border-radius: 10px;
    gap: 12px;
    grid-template-columns: 46px minmax(0, 1fr);
    min-height: auto;
    padding: 14px;
  }

  .module-row-icon {
    border-radius: 10px;
    height: 44px;
    width: 44px;
  }

  .module-row-icon i {
    font-size: 1.35rem;
  }

  .module-row-copy {
    gap: 5px;
  }

  .module-row-title {
    font-size: 1rem;
  }

  .module-row-desc {
    font-size: 0.84rem;
    line-height: 1.4;
  }

  .module-row-alert {
    border-radius: 8px;
    grid-column: 1 / -1;
    justify-self: stretch;
    max-width: none;
    padding: 8px 10px;
    text-align: left;
  }

  .module-row-action {
    border-top: 1px solid #edf2f4;
    grid-column: 1 / -1;
    justify-content: space-between;
    padding-top: 12px;
    width: 100%;
  }

  .module-row-action i {
    height: 38px;
    width: 38px;
  }

  .module-empty-state {
    align-items: flex-start;
    border: 1px solid #e1e8eb;
    border-radius: 10px;
    padding: 18px;
  }

  .module-empty-icon {
    height: 46px;
    width: 46px;
  }

  .dashboard-footer {
    flex-wrap: wrap;
    font-size: 0.74rem;
    margin-top: 12px;
    padding: 18px 0 24px;
  }
}
</style>
